import { useMemo } from "react";
import { Link } from "wouter";
import {
  AlertBanner,
  engineWarningsToAlerts,
  type AlertItem,
} from "../components/fiscal/AlertBanner.js";
import { FiscalYearSelector } from "../components/fiscal/FiscalYearSelector.js";
import { ForecastPanel } from "../components/fiscal/ForecastPanel.js";
import { KpiCard } from "../components/fiscal/KpiCard.js";
import { FiscalCard, FiscalShell, LoadingSkeleton } from "../components/fiscal/Shell.js";
import { useFiscalSelection } from "../hooks/useFiscalSelection.js";
import { useT } from "../i18n/index.js";
import {
  computeDelais,
  computeEcartSeuil,
  computeFiscalAlerts,
  findLastBalanceEntry,
} from "../lib/fiscal-derived.js";
import { formatDays, formatMoney, formatPct, toNumber } from "../lib/format-fr.js";
import { trpc } from "../lib/trpc.js";

export default function FiscalDashboard() {
  const { companyId, fiscalYearId, setCompanyId, setFiscalYearId } = useFiscalSelection();
  const t = useT();

  const calcQuery = trpc.fiscalCalculations.getByFiscalYear.useQuery(
    { companyId: companyId ?? 0, fiscalYearId: fiscalYearId ?? 0 },
    { enabled: companyId !== null && fiscalYearId !== null },
  );
  const aggQuery = trpc.fiscalAggregates.getByFiscalYear.useQuery(
    { companyId: companyId ?? 0, fiscalYearId: fiscalYearId ?? 0 },
    { enabled: companyId !== null && fiscalYearId !== null },
  );
  const fyDetailQuery = trpc.fiscalYears.getById.useQuery(
    { companyId: companyId ?? 0, fiscalYearId: fiscalYearId ?? 0 },
    { enabled: companyId !== null && fiscalYearId !== null },
  );
  const bankBalanceQuery = trpc.bankTransactions.companyBalance.useQuery(
    fiscalYearId !== null
      ? { companyId: companyId ?? 0, fiscalYearId }
      : { companyId: companyId ?? 0 },
    { enabled: companyId !== null, retry: false },
  );

  const forecastQuery = trpc.fiscalForecasts.getByFiscalYear.useQuery(
    { companyId: companyId ?? 0, fiscalYearId: fiscalYearId ?? 0 },
    {
      enabled: companyId !== null && fiscalYearId !== null && fyDetailQuery.data?.status === "open",
      retry: false,
    },
  );
  // Baseline FY anterior (para mostrar label "Réel N-1 (YYYY)" no painel).
  const baselineYearLabelQuery = trpc.fiscalYears.getById.useQuery(
    {
      companyId: companyId ?? 0,
      fiscalYearId: forecastQuery.data?.baselineFiscalYearId ?? 0,
    },
    {
      enabled:
        companyId !== null &&
        forecastQuery.data?.baselineFiscalYearId !== undefined &&
        forecastQuery.data?.baselineFiscalYearId !== null,
    },
  );

  const utils = trpc.useUtils();
  const computeMutation = trpc.fiscalCalculations.compute.useMutation({
    onSuccess: () => {
      utils.fiscalCalculations.invalidate();
      utils.fiscalAggregates.invalidate();
    },
  });
  const forecastMutation = trpc.fiscalForecasts.compute.useMutation({
    onSuccess: () => {
      utils.fiscalForecasts.invalidate();
      utils.fiscalCalculations.invalidate();
      utils.fiscalAggregates.invalidate();
    },
  });

  const calc = calcQuery.data;
  const entries = fyDetailQuery.data?.entries ?? [];
  const lastBalanceEntry = useMemo(() => findLastBalanceEntry(entries), [entries]);

  const delais = useMemo(
    () =>
      calc
        ? computeDelais({
            caHt: calc.caHt,
            achatsConsommes: calc.achatsConsommes,
            nbJoursExercice: calc.nbJoursExercice,
            lastBalanceEntry,
          })
        : null,
    [calc, lastBalanceEntry],
  );

  const ecart = useMemo(
    () =>
      calc
        ? computeEcartSeuil({
            caHt: calc.caHt,
            seuilEconomique: calc.seuilEconomique,
          })
        : null,
    [calc],
  );

  const alerts = useMemo<AlertItem[]>(() => {
    if (!calc) return [];
    const a = computeFiscalAlerts({
      caHt: calc.caHt,
      margeGlobaleCumulative: calc.margeGlobaleCumulative,
      chargesPersonnel: calc.chargesPersonnel,
      vaCumulative: calc.vaCumulative,
      tresorerieEntry: calc.tresorerieEntry,
      ecartPctOfCa: ecart?.ecartPctOfCa ?? null,
      dso: delais?.dso ?? null,
    });
    const out: AlertItem[] = [];
    if (a.tresorerieNegativa?.triggered) {
      out.push({
        severity: "critical",
        message: `${t("fiscal.alertNegativeCashPre")} : ${formatMoney(a.tresorerieNegativa.tresorerie)}.`,
        key: "tresorerie-negative",
      });
    }
    if (a.margeBasse?.triggered) {
      out.push({
        severity: "warning",
        message: `${t("fiscal.alertLowMarginPre")} (${formatPct(a.margeBasse.tauxMarge, { alreadyPct: true })}) — ${t("fiscal.alertLowMarginSuf")}`,
        key: "marge-low",
      });
    }
    if (a.delaiClientsAlto?.triggered) {
      out.push({
        severity: "warning",
        message: `${t("fiscal.alertDsoHighPre")} (${formatDays(a.delaiClientsAlto.dso)}) — ${t("fiscal.alertDsoHighSuf")}`,
        key: "dso-high",
      });
    }
    if (a.ecartSeuilEstreito?.triggered) {
      out.push({
        severity: "warning",
        message: `${t("fiscal.alertGapNarrowPre")} (${formatPct(a.ecartSeuilEstreito.ecartPctOfCa, { alreadyPct: true })} ${t("fiscal.ofCaSuf")}) — ${t("fiscal.alertGapNarrowSuf")}`,
        key: "ecart-low",
      });
    }
    if (a.chargesPersonnelAltas?.triggered) {
      out.push({
        severity: "warning",
        message: `${t("fiscal.alertPersonnelHighPre")} (${formatPct(a.chargesPersonnelAltas.ratio, { alreadyPct: true })} ${t("fiscal.ofVaSuf")}) — ${t("fiscal.alertPersonnelHighSuf")}`,
        key: "personnel-high",
      });
    }
    out.push(...engineWarningsToAlerts(calc.warnings as string[] | null));
    // Warnings específicos do forecast (IS > 5k, RN proj < 0, CA proj < seuil)
    out.push(
      ...engineWarningsToAlerts(forecastQuery.data?.warnings as string[] | null | undefined),
    );
    return out;
  }, [calc, ecart, delais, forecastQuery.data, t]);

  const toolbar = (
    <div className="flex items-center justify-between gap-4 flex-wrap">
      <FiscalYearSelector
        companyId={companyId}
        fiscalYearId={fiscalYearId}
        onChangeCompany={setCompanyId}
        onChangeFiscalYear={(fy) => setFiscalYearId(fy?.id ?? null)}
      />
      {companyId !== null &&
        fiscalYearId !== null &&
        (fyDetailQuery.data?.status === "closed" ? (
          <span
            className="rounded-md border border-amber-500/40 bg-amber-500/10 text-amber-300 px-3 py-1.5 text-xs cursor-help"
            title={t("fiscal.closedRecomputeTitle")}
          >
            🔒 {t("fiscal.closedBadge")}
          </span>
        ) : (
          <button
            onClick={() => computeMutation.mutate({ companyId, fiscalYearId })}
            disabled={computeMutation.isPending || fyDetailQuery.isLoading}
            className="rounded-md border border-lime/40 hover:border-lime hover:text-lime text-lime px-3 py-1.5 text-xs disabled:opacity-50"
          >
            {computeMutation.isPending ? t("fiscal.recomputing") : `↻ ${t("fiscal.recomputeBtn")}`}
          </button>
        ))}
    </div>
  );

  return (
    <FiscalShell subtitle={t("fiscal.subtitle")} toolbar={toolbar}>
      {companyId === null && (
        <FiscalCard title={t("fiscal.selectCompanyTitle")}>
          <p className="text-sm text-neutral-400">{t("fiscal.selectCompanyBody")}</p>
        </FiscalCard>
      )}

      {companyId !== null && fiscalYearId === null && (
        <FiscalCard title={t("fiscal.noFiscalYearTitle")}>
          <p className="text-sm text-neutral-400">
            {t("fiscal.noFiscalYearBody")}{" "}
            <Link href="/fiscal/entries" className="text-lime hover:underline">
              {t("fiscal.goToEntries")}
            </Link>{" "}
            {t("fiscal.toCreateOneSuf")}
          </p>
        </FiscalCard>
      )}

      {fiscalYearId !== null && calcQuery.isLoading && (
        <FiscalCard>
          <LoadingSkeleton rows={6} />
        </FiscalCard>
      )}

      {fiscalYearId !== null && !calcQuery.isLoading && calc === null && (
        <FiscalCard title={t("fiscal.noKpisTitle")}>
          <p className="text-sm text-neutral-400">
            {t("fiscal.noKpisBodyPre")} <strong>↻ {t("fiscal.recomputeBtn")}</strong>{" "}
            {t("fiscal.noKpisBodySuf")}
          </p>
          {computeMutation.error && (
            <p className="text-sm text-red-400 mt-2">{computeMutation.error.message}</p>
          )}
        </FiscalCard>
      )}

      {calc && (
        <>
          <AlertBanner alerts={alerts} />

          <div className="flex items-baseline justify-between gap-3 flex-wrap border-l-2 border-lime/40 pl-3">
            <h2 className="text-sm uppercase tracking-wider text-lime">
              {t("fiscal.actualCumulative")}
              {(() => {
                const m = Number(calc.moisDecoulus);
                return Number.isFinite(m) && m > 0
                  ? ` · ${m.toFixed(1).replace(".", ",")} ${t("fiscal.monthsElapsedSuf")}`
                  : "";
              })()}
            </h2>
            <span className="text-[11px] text-neutral-500">
              {t("fiscal.computedAtPre")}{" "}
              {calc.lastComputedAt ? new Date(calc.lastComputedAt).toLocaleString("fr-FR") : "—"}
            </span>
          </div>

          {/* Fila 1: CA, Marge, Résultat, EBE */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            <KpiCard
              label={t("fiscal.caLabel")}
              value={formatMoney(calc.caHt)}
              secondaryLabel={t("fiscal.annualized")}
              secondaryValue={formatMoney(calc.caHt)}
              tooltip={t("fiscal.caTooltip")}
              subtitle={`${t("fiscal.exercisePre")} ${fyDetailQuery.data?.label ?? ""}`}
            />
            <KpiCard
              label={t("fiscal.grossMarginLabel")}
              value={formatMoney(calc.margeGlobaleCumulative)}
              secondaryLabel={t("fiscal.pctOnCa")}
              secondaryValue={formatPct(
                toNumber(calc.caHt) && toNumber(calc.caHt)! > 0
                  ? (toNumber(calc.margeGlobaleCumulative)! / toNumber(calc.caHt)!) * 100
                  : null,
                { alreadyPct: true },
              )}
              tone={
                toNumber(calc.caHt) &&
                toNumber(calc.caHt)! > 0 &&
                (toNumber(calc.margeGlobaleCumulative)! / toNumber(calc.caHt)!) * 100 < 15
                  ? "amber"
                  : "neutral"
              }
              tooltip={t("fiscal.grossMarginTooltip")}
            />
            <KpiCard
              label={t("fiscal.netResultLabel")}
              value={formatMoney(calc.resultatNetCumulative)}
              secondaryLabel={t("fiscal.pctOnCa")}
              secondaryValue={formatPct(
                toNumber(calc.caHt) && toNumber(calc.caHt)! > 0
                  ? (toNumber(calc.resultatNetCumulative)! / toNumber(calc.caHt)!) * 100
                  : null,
                { alreadyPct: true },
              )}
              tone={toNumber(calc.resultatNetCumulative)! < 0 ? "red" : "neutral"}
              tooltip={t("fiscal.netResultTooltip")}
            />
            <KpiCard
              label={t("fiscal.ebeLabel")}
              value={formatMoney(calc.ebeCumulative)}
              secondaryLabel={t("fiscal.annualized")}
              secondaryValue={formatMoney(calc.ebeAnnualized)}
              tooltip={t("fiscal.ebeTooltip")}
            />
          </div>

          {/* Fila 2: CAF, Trésorerie comptable, Solde bancaire, Seuil */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            <KpiCard
              label={t("fiscal.cafLabel")}
              value={formatMoney(calc.cafCumulative)}
              secondaryLabel={t("fiscal.annualized")}
              secondaryValue={formatMoney(calc.cafAnnualized)}
              tooltip={t("fiscal.cafTooltip")}
            />
            <KpiCard
              label={t("fiscal.accountingCashLabel")}
              value={formatMoney(calc.tresorerieEntry)}
              subtitle={
                calc.balanceMonth && calc.balanceYear
                  ? `${t("fiscal.balanceSheetPre")} ${String(calc.balanceMonth).padStart(2, "0")}/${calc.balanceYear}`
                  : t("fiscal.noBalanceSheet")
              }
              tone={(toNumber(calc.tresorerieEntry) ?? 0) < 0 ? "red" : "neutral"}
              tooltip={t("fiscal.accountingCashTooltip")}
            />
            <KpiCard
              label={t("fiscal.bankBalanceLabel")}
              value={
                bankBalanceQuery.error
                  ? "—"
                  : bankBalanceQuery.data === undefined
                    ? "…"
                    : bankBalanceQuery.data.balance === null
                      ? "—"
                      : formatMoney(bankBalanceQuery.data.balance)
              }
              loading={bankBalanceQuery.isLoading}
              subtitle={
                bankBalanceQuery.error
                  ? t("fiscal.unavailable")
                  : bankBalanceQuery.data === undefined
                    ? undefined
                    : bankBalanceQuery.data.balance === null
                      ? t("fiscal.noTransactionsImported")
                      : `${bankBalanceQuery.data.accountsCount} ${t("fiscal.accountsSuf")} · ${bankBalanceQuery.data.transactionsCount} ${t("fiscal.txSuf")}`
              }
              tooltip={
                bankBalanceQuery.data?.balance === null
                  ? t("fiscal.bankBalanceEmptyTooltip")
                  : t("fiscal.bankBalanceTooltip")
              }
            />
            <KpiCard
              label={t("fiscal.breakEvenLabel")}
              value={formatMoney(calc.seuilEconomique)}
              secondaryLabel={t("fiscal.financialLabel")}
              secondaryValue={formatMoney(calc.seuilFinancier)}
              tooltip={t("fiscal.breakEvenTooltip")}
            />
          </div>

          {/* Fila 3: Écart seuil, Point mort, Délai clients, Délai fournisseurs */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            <KpiCard
              label={t("fiscal.thresholdGapLabel")}
              value={formatMoney(ecart?.ecart ?? null)}
              secondaryLabel={t("fiscal.pctDuCa")}
              secondaryValue={formatPct(ecart?.ecartPctOfCa, { alreadyPct: true })}
              tone={
                (ecart?.ecartPctOfCa ?? 100) < 10
                  ? "amber"
                  : (ecart?.ecart ?? 0) < 0
                    ? "red"
                    : "positive"
              }
              tooltip={t("fiscal.thresholdGapTooltip")}
            />
            <KpiCard
              label={t("fiscal.pointMortLabel")}
              value={formatDays(calc.pointMortJours)}
              subtitle={t("fiscal.commercialConvention")}
              tooltip={t("fiscal.pointMortTooltip")}
            />
            <KpiCard
              label={t("fiscal.dsoLabel")}
              value={formatDays(delais?.dso ?? null)}
              secondaryLabel={
                delais?.bilanMonth
                  ? `${t("fiscal.balanceSheetPre")} ${String(delais.bilanMonth).padStart(2, "0")}/${delais.bilanYear}`
                  : undefined
              }
              tone={(delais?.dso ?? 0) > 60 ? "amber" : "neutral"}
              tooltip={t("fiscal.dsoTooltip")}
            />
            <KpiCard
              label={t("fiscal.dpoLabel")}
              value={formatDays(delais?.dpo ?? null)}
              secondaryLabel={
                delais?.bilanMonth
                  ? `${t("fiscal.balanceSheetPre")} ${String(delais.bilanMonth).padStart(2, "0")}/${delais.bilanYear}`
                  : undefined
              }
              tooltip={t("fiscal.dpoTooltip")}
            />
          </div>

          {/* Fila 4: VA, Résultat exploitation, Impôt sociétés, Charges externes */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            <KpiCard
              label={t("fiscal.vaLabel")}
              value={formatMoney(calc.vaCumulative)}
              secondaryLabel={t("fiscal.pctOnCa")}
              secondaryValue={formatPct(
                toNumber(calc.caHt) && toNumber(calc.caHt)! > 0
                  ? (toNumber(calc.vaCumulative)! / toNumber(calc.caHt)!) * 100
                  : null,
                { alreadyPct: true },
              )}
              tone={(toNumber(calc.vaCumulative) ?? 0) < 0 ? "red" : "neutral"}
              tooltip={t("fiscal.vaTooltip")}
            />
            <KpiCard
              label={t("fiscal.operatingResultLabel")}
              value={formatMoney(calc.resultatExploitationCumulative)}
              secondaryLabel={t("fiscal.pctOnCa")}
              secondaryValue={formatPct(
                toNumber(calc.caHt) && toNumber(calc.caHt)! > 0
                  ? (toNumber(calc.resultatExploitationCumulative)! / toNumber(calc.caHt)!) * 100
                  : null,
                { alreadyPct: true },
              )}
              tone={(toNumber(calc.resultatExploitationCumulative) ?? 0) < 0 ? "red" : "neutral"}
              tooltip={t("fiscal.operatingResultTooltip")}
            />
            <KpiCard
              label={t("fiscal.corporateTaxLabel")}
              value={formatMoney(calc.impotSocietes)}
              secondaryLabel={t("fiscal.pctOnRe")}
              secondaryValue={formatPct(
                toNumber(calc.resultatExploitationCumulative) &&
                  toNumber(calc.resultatExploitationCumulative)! > 0
                  ? (toNumber(calc.impotSocietes)! /
                      toNumber(calc.resultatExploitationCumulative)!) *
                      100
                  : null,
                { alreadyPct: true },
              )}
              tone={(toNumber(calc.impotSocietes) ?? 0) > 5000 ? "amber" : "neutral"}
              tooltip={t("fiscal.corporateTaxTooltip")}
            />
            <KpiCard
              label={t("fiscal.externalChargesLabel")}
              value={formatMoney(calc.chargesExternesTotal)}
              secondaryLabel={t("fiscal.pctOnCa")}
              secondaryValue={formatPct(
                toNumber(calc.caHt) && toNumber(calc.caHt)! > 0
                  ? (toNumber(calc.chargesExternesTotal)! / toNumber(calc.caHt)!) * 100
                  : null,
                { alreadyPct: true },
              )}
              tooltip={t("fiscal.externalChargesTooltip")}
            />
          </div>

          {/* Painel Projection de Clôture — só para FY status='open' */}
          {fyDetailQuery.data?.status === "open" && (
            <>
              {forecastQuery.data ? (
                <ForecastPanel
                  forecast={forecastQuery.data}
                  fiscalYearLabel={fyDetailQuery.data.label}
                  baselineYearLabel={baselineYearLabelQuery.data?.label ?? null}
                  onRecompute={() =>
                    forecastMutation.mutate({
                      companyId: companyId!,
                      fiscalYearId: fiscalYearId!,
                    })
                  }
                  isRecomputing={forecastMutation.isPending}
                />
              ) : (
                <FiscalCard
                  title={t("fiscal.forecastTitle")}
                  subtitle={t("fiscal.forecastEmptySubtitle")}
                  action={
                    <button
                      onClick={() =>
                        forecastMutation.mutate({
                          companyId: companyId!,
                          fiscalYearId: fiscalYearId!,
                        })
                      }
                      disabled={forecastMutation.isPending}
                      className="rounded-md border border-sky-500/40 hover:border-sky-500 hover:text-sky-200 text-sky-300 px-3 py-1.5 text-xs disabled:opacity-50"
                    >
                      {forecastMutation.isPending
                        ? t("fiscal.generating")
                        : `↻ ${t("fiscal.generateForecastBtn")}`}
                    </button>
                  }
                >
                  {forecastMutation.error && (
                    <p className="text-sm text-red-400">{forecastMutation.error.message}</p>
                  )}
                </FiscalCard>
              )}
            </>
          )}
          {fyDetailQuery.data?.status === "closed" && (
            <div className="rounded-lg border border-neutral-800 bg-neutral-900/40 px-5 py-3 text-sm text-neutral-400">
              {t("fiscal.closedForecastNote")}
            </div>
          )}

          {/* Fila 4: contexto aggregate */}
          <FiscalCard
            title={t("fiscal.contextTitle")}
            subtitle={`${t("fiscal.computationPre")}: ${calc.lastComputedAt ? new Date(calc.lastComputedAt).toLocaleString("fr-FR") : "—"}`}
          >
            <dl className="grid grid-cols-2 sm:grid-cols-4 gap-y-2 gap-x-6 text-sm">
              <div>
                <dt className="text-xs uppercase tracking-wider text-neutral-500">
                  {t("fiscal.caHtLabel")}
                </dt>
                <dd className="font-mono">{formatMoney(calc.caHt)}</dd>
              </div>
              <div>
                <dt className="text-xs uppercase tracking-wider text-neutral-500">
                  {t("fiscal.purchasesConsumedLabel")}
                </dt>
                <dd className="font-mono">{formatMoney(calc.achatsConsommes)}</dd>
              </div>
              <div>
                <dt className="text-xs uppercase tracking-wider text-neutral-500">
                  {t("fiscal.otherVarChargesLabel")}
                </dt>
                <dd className="font-mono">{formatMoney(calc.autresChargesVariables)}</dd>
              </div>
              <div>
                <dt className="text-xs uppercase tracking-wider text-neutral-500">
                  {t("fiscal.externalChargesLabel")}
                </dt>
                <dd className="font-mono">{formatMoney(calc.chargesExternesTotal)}</dd>
              </div>
              <div>
                <dt className="text-xs uppercase tracking-wider text-neutral-500">
                  {t("fiscal.nbMonthsFyLabel")}
                </dt>
                <dd className="font-mono">{toNumber(calc.nbMoisExercice)?.toFixed(2) ?? "—"}</dd>
              </div>
              <div>
                <dt className="text-xs uppercase tracking-wider text-neutral-500">
                  {t("fiscal.monthsElapsedLabel")}
                </dt>
                <dd className="font-mono">{toNumber(calc.moisDecoulus)?.toFixed(2) ?? "—"}</dd>
              </div>
              <div>
                <dt className="text-xs uppercase tracking-wider text-neutral-500">
                  {t("fiscal.aggregateLabel")}
                </dt>
                <dd className="font-mono text-xs">
                  {aggQuery.data?.lastComputedAt
                    ? new Date(aggQuery.data.lastComputedAt).toLocaleDateString("fr-FR")
                    : "—"}
                </dd>
              </div>
              <div>
                <dt className="text-xs uppercase tracking-wider text-neutral-500">
                  {t("fiscal.fyStatusLabel")}
                </dt>
                <dd className="text-xs">{fyDetailQuery.data?.status ?? "—"}</dd>
              </div>
            </dl>
          </FiscalCard>
        </>
      )}
    </FiscalShell>
  );
}
